"use client";

import { useRouter } from "../navigation";
import { useState } from "react";
import { deleteProject, saveProject } from "../actions";
import type { MediaItem, ProjectRecord } from "../types";
import { LanguageBar, type EditLang } from "./locale";
import { ImageField } from "./media";
import { SaveBar, useSave } from "./SaveBar";
import { CARD, ConfirmButton, LinesEditor, PageTitle, Select, StatusSwitch, TextArea, TextField } from "./ui";

const slugify = (t: string) =>
  t.toLowerCase().normalize("NFKD").replace(/[̀-ͯ]/g, "").replace(/&/g, " and ").replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 80);

/** Create or edit a project (shown on the Projects page and the home page's sideways rail, in list order), in English
 * and Arabic. Image, card colour and links are shared. */
export function ProjectEditor({ project, media }: { project: ProjectRecord | null; media: MediaItem[] }) {
  const router = useRouter();
  const isNew = !project;
  const [lang, setLang] = useState<EditLang>("en");
  const [form, setForm] = useState(() => ({
    id: project?.id ?? "",
    status: project?.status ?? ("draft" as const),
    name: project?.name ?? "",
    slug: project?.slug ?? "",
    client: project?.client ?? "",
    tags: project?.tags ?? [],
    frame: project?.frame ?? ("teal" as ProjectRecord["frame"]),
    image: project?.image ? { url: project.image, alt: project.alt ?? "" } : null,
    href: project?.href ?? "",
    summary: project?.summary ?? "",
  }));
  const [ar, setAr] = useState(() => ({
    name: project?.ar?.name ?? "",
    client: project?.ar?.client ?? "",
    tags: project?.ar?.tags ?? [],
    summary: project?.ar?.summary ?? "",
    alt: project?.ar?.alt ?? "",
  }));
  const [slugTouched, setSlugTouched] = useState(!isNew);
  const { pending, notice, dirty, markDirty, run } = useSave();
  const set = <K extends keyof typeof form>(field: K, value: (typeof form)[K]) => {
    markDirty();
    setForm((f) => ({ ...f, [field]: value, ...(field === "name" && !slugTouched ? { slug: slugify(String(value)) } : {}) }));
  };
  const setArField = <K extends keyof typeof ar>(field: K, value: (typeof ar)[K]) => {
    markDirty();
    setAr((a) => ({ ...a, [field]: value }));
  };

  const save = () =>
    run(
      () => saveProject({ ...form, image: form.image?.url ?? null, alt: form.image?.alt, href: form.href || null, ar }),
      form.status === "published" ? "Saved and published." : "Saved as a draft (not on the website).",
      (r) => r.ok && isNew && r.id && router.replace(`/admin/projects/${r.id}`),
    );

  return (
    <div>
      <PageTitle title={isNew ? "New project" : "Edit project"} />
      <LanguageBar
        lang={lang}
        onChange={setLang}
        missing={{ ar: !ar.name.trim() }}
        arabicNote="Arabic text for /ar. Fields left empty show the English text. Image, card colour, links and status are shared: change them on the English tab."
      />
      {lang === "en" ? (
        <section className={`${CARD} flex flex-col gap-5`}>
          <TextField label="Project name" required value={form.name} onChange={(v) => set("name", v)} max={60} />
          <TextField
            label="Web address"
            required
            value={form.slug}
            onChange={(v) => {
              setSlugTouched(true);
              set("slug", slugify(v));
            }}
            hint="Used for the case study address later."
          />
          <TextField label="Client" value={form.client} onChange={(v) => set("client", v)} />
          <LinesEditor label="Tags" value={form.tags} onChange={(v) => set("tags", v)} placeholder="e.g. Brand" addLabel="Add tag" hint="Short words, e.g. Brand, Identity, Website." />
          <ImageField label="Main image" value={form.image} onChange={(v) => set("image", v)} media={media} hint="Landscape, at least 1600 × 1000. Without one, the card shows a placeholder." />
          <Select
            label="Card colour"
            value={form.frame}
            onChange={(v) => set("frame", v as ProjectRecord["frame"])}
            options={[
              { value: "teal", label: "Deep Teal" },
              { value: "offwhite", label: "Off-white" },
              { value: "midnight", label: "Midnight" },
            ]}
          />
          <TextArea label="Summary" value={form.summary} onChange={(v) => set("summary", v)} rows={3} />
          <TextField label="Live link" value={form.href} onChange={(v) => set("href", v)} placeholder="https://" hint="Optional: the client's live website or campaign." />
        </section>
      ) : (
        <section className={`${CARD} flex flex-col gap-5`}>
          <TextField label="Project name" lang="ar" value={ar.name} onChange={(v) => setArField("name", v)} max={60} placeholder={form.name} />
          <TextField label="Client" lang="ar" value={ar.client} onChange={(v) => setArField("client", v)} placeholder={form.client} />
          <LinesEditor label="Tags" lang="ar" value={ar.tags} onChange={(v) => setArField("tags", v)} addLabel="Add tag" hint="Short words, e.g. علامة تجارية، هوية بصرية." />
          {form.image && <TextField label="Image description (alt text)" lang="ar" value={ar.alt} onChange={(v) => setArField("alt", v)} placeholder={form.image.alt} hint="Describes the image for people who can't see it." />}
          <TextArea label="Summary" lang="ar" value={ar.summary} onChange={(v) => setArField("summary", v)} rows={3} />
        </section>
      )}
      <SaveBar onSave={save} pending={pending} dirty={dirty} notice={notice}>
        <StatusSwitch value={form.status} onChange={(v) => set("status", v)} />
        {!isNew && <ConfirmButton question="Delete this project (both languages)?" onConfirm={() => run(() => deleteProject(form.id), "Deleted.", (r) => r.ok && (router.replace("/admin/projects"), router.refresh()))} />}
      </SaveBar>
    </div>
  );
}
